// Camada entre o índice de conteúdo (frontmatter, gerado pelo plugin do Vite) e
// as telas. Nenhum componente importa `virtual:docs-index` direto.

import { pages as allPages } from "virtual:docs-index";

import { locales, nav } from "../../docs.config.ts";
import { type NavSection, type Page, type SidebarGroup, type SidebarGroupConfig } from "../../types/content.ts";

// Os módulos .mdx propriamente ditos, carregados sob demanda. As chaves são o
// mesmo caminho que o plugin guardou em `page.file`, então o casamento entre
// metadado e conteúdo é por construção, não por convenção.
const loaders = import.meta.glob("/content/**/*.mdx");

export const pages = allPages;

const navById = new Map(nav.map((n) => [n.id, n]));

export function sectionConfig(sectionId: string | undefined): NavSection | null {
  return (sectionId ? navById.get(sectionId) : undefined) ?? null;
}

// ── Seções desligadas ────────────────────────────────────────────────────────
// `enabled: false` tira a seção do ar; `hidden: true` só a tira do menu e da
// busca, mantendo o link direto. Ver o comentário em docs.config.

// Rota acessível? Só `enabled: false` bloqueia.
export function isSectionEnabled(sectionId: string): boolean {
  const config = navById.get(sectionId);
  return !!config && config.enabled !== false;
}

// Aparece no menu do topo e nos resultados de busca?
export function isSectionVisible(sectionId: string): boolean {
  const config = navById.get(sectionId);
  return !!config && config.enabled !== false && config.hidden !== true;
}

// Itens do menu do topo, já filtrados.
export const visibleNav = nav.filter((item) => isSectionVisible(item.id));

// ── Resolução de página ──────────────────────────────────────────────────────

function match(locale: string, section: string, slug: string | null): Page | undefined {
  return allPages.find((p) => p.locale === locale && p.section === section && p.slug === (slug ?? null));
}

// Devolve { page, untranslated }. Quando o idioma pedido não tem a página mas o
// fallback tem, entrega a do fallback marcada — melhor mostrar o conteúdo em
// português com um aviso do que um 404 em cima de uma tradução pendente.
export function resolvePage(
  locale: string,
  section: string,
  slug: string | null = null
): { page: Page | null; untranslated: boolean } {
  const exact = match(locale, section, slug);
  if (exact) return { page: exact, untranslated: false };

  if (locale !== locales.fallback) {
    const fallback = match(locales.fallback, section, slug);
    if (fallback) return { page: fallback, untranslated: true };
  }
  return { page: null, untranslated: false };
}

export function loadPage(page: Page): Promise<MdxModule> {
  const loader = loaders[page.file];
  if (!loader) throw new Error(`Sem loader para ${page.file}`);
  return loader() as Promise<MdxModule>;
}

// ── Sidebar de uma seção de guias ────────────────────────────────────────────

// Itens do idioma corrente, completados com os do fallback que ainda não foram
// traduzidos — a sidebar mostra a mesma estrutura em todos os idiomas.
function pagesForSection(locale: string, section: string): Page[] {
  const listed = allPages.filter((p) => !p.hidden);
  const own = listed.filter((p) => p.locale === locale && p.section === section && p.slug);
  if (locale === locales.fallback) return own;

  const seen = new Set(own.map((p) => p.slug));
  const inherited = listed.filter(
    (p) => p.locale === locales.fallback && p.section === section && p.slug && !seen.has(p.slug)
  );
  return [...own, ...inherited];
}

export function sidebarForSection(locale: string, sectionId: string): SidebarGroup[] {
  const config = sectionConfig(sectionId);
  if (config?.type !== "guides") return [];

  const items = pagesForSection(locale, sectionId);
  const used = new Set();

  // Todo nó declarado pode ter as duas coisas: as páginas cujo `group` casa com o
  // seu id, e grupos declarados dentro dele. É o que permite "Integrações" ter as
  // suas quatro páginas e ainda conter o "Motor V1" recolhido — o motor é uma das
  // integrações, não um par dela.
  //
  // A montagem é recursiva: hoje só existem dois níveis, mas travar a
  // profundidade aqui não economizaria nada e daria um erro pior de entender.
  const build = (declared: SidebarGroupConfig[]): SidebarGroup[] =>
    declared
      .map((g): SidebarGroup | null => {
        const own = items.filter((p) => p.group === g.id).sort((a, b) => a.order - b.order);
        for (const p of own) used.add(p.file);
        const groups = g.groups ? build(g.groups) : [];
        // Nó vazio viraria um rótulo sem nada embaixo, ou uma seta que abre o nada.
        if (!own.length && !groups.length) return null;
        return {
          id: g.id,
          label: g.label,
          collapsed: g.collapsed,
          items: own,
          groups,
        };
      })
      .filter((g): g is SidebarGroup => g !== null);

  const tree = build(config.sidebar || []);

  // Páginas com `group` que não existe na config (ou sem group nenhum) iriam
  // sumir da navegação sem deixar rastro. Ficam num grupo extra no fim.
  const orphans = items.filter((p) => !used.has(p.file)).sort((a, b) => a.order - b.order);
  if (orphans.length) {
    tree.push({ id: "__outros", label: { pt: "Outros", en: "Other", es: "Otros" }, items: orphans });
  }

  return tree;
}

// Primeira página de uma seção — destino de /:locale/:section sem slug.
// Percorre a árvore porque o primeiro nó pode não ter páginas próprias, só
// grupos dentro.
export function firstPageOf(locale: string, sectionId: string): Page | null {
  const firstIn = (nodes: SidebarGroup[]): Page | null => {
    for (const n of nodes) {
      if (n.items?.length) return n.items[0] ?? null;
      const found = firstIn(n.groups || []);
      if (found) return found;
    }
    return null;
  };
  return firstIn(sidebarForSection(locale, sectionId));
}

// ── Rota inicial ─────────────────────────────────────────────────────────────

export function defaultRoute(locale: string = locales.default): string {
  // A primeira seção do menu pode estar desligada — mandar o visitante para uma
  // rota que redireciona de volta para cá seria um laço.
  const first = visibleNav[0];
  if (!first) return `/${locale}`;
  if (first.type === "guides") {
    const page = firstPageOf(locale, first.id);
    return page ? `/${locale}/${first.id}/${page.slug}` : `/${locale}/${first.id}`;
  }
  return `/${locale}/${first.id}`;
}
